/**
 * The in-page "new chat" shortcuts. Both are live:
 * - ⌘T / Ctrl+T. Kourai is also the browser's new tab page, so in Chrome this key already lands on
 *   a fresh Kourai tab (a new chat). Chrome never hands it to the page, but other hosts do (the
 *   Kourai Electron browser's web contents, the web app in some browsers), so we still listen.
 * - ⌘⇧O / Ctrl+Shift+O, the same keys as Claude and ChatGPT. Chrome lets a page take these, so
 *   it starts a new chat in place on every host.
 * The side panel's browser-level shortcut is a manifest `commands` entry instead (ADMIN-214).
 */

type ShortcutEvent = Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">;

/** Whether the platform uses ⌘ as its main modifier. */
export function isMacPlatform(
  platform: string = typeof navigator === "undefined" ? "" : navigator.platform,
): boolean {
  return /mac|iphone|ipad|ipod/i.test(platform);
}

/** True for either new chat chord: the platform's modifier + T, or + Shift + O. No other keys held. */
export function isNewChatShortcut(e: ShortcutEvent, mac: boolean = isMacPlatform()): boolean {
  const mod = mac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  if (!mod || e.altKey) return false;
  const key = e.key.toLowerCase();
  return (key === "t" && !e.shiftKey) || (key === "o" && e.shiftKey);
}

/** The keys to show for each shortcut, one entry per key cap: [⌘T, ⌘⇧O]. */
export function newChatShortcutKeys(mac: boolean = isMacPlatform()): string[][] {
  return mac
    ? [
        ["⌘", "T"],
        ["⌘", "⇧", "O"],
      ]
    : [
        ["Ctrl", "T"],
        ["Ctrl", "Shift", "O"],
      ];
}

/** The shortcuts as one string, for tooltips and aria labels: "⌘T or ⌘⇧O". */
export function newChatShortcutLabel(mac: boolean = isMacPlatform()): string {
  return newChatShortcutKeys(mac)
    .map((keys) => keys.join(mac ? "" : "+"))
    .join(" or ");
}
